import { Flex, HStack, Stack, Text, Tooltip } from '@chakra-ui/react';
import type { ExpertFormData } from '../../types';
import { ExpertResource, IntegrationSourceType, Maybe, ResourceSyncError } from '@/client/services/api/graphql/gql/graphql';
import WarmNeutralEllipsisMenu from '@/client/components/shared/menus/WarmNeutralEllipsisMenu';
import { useExpertsStore } from '../../../expertsStore';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import GoogleFileTypeIcon from './GoogleFileTypeIcon';
import { CheckCircle } from '@/client/components/icons/individual/CheckCircle';
import { WarningTriangle } from '@/client/components/icons/individual/WarningTriangle';


interface GoogleDriveUploadFileListProps {
  /** Maximum number of files to render in the list */
  renderLimit?: number;
}

export default function GoogleDriveUploadFileList({ renderLimit }: GoogleDriveUploadFileListProps) {
  const { t } = useTranslation();
  const { isEditingExpert } = useExpertsStore();
  const { watch, setValue } = useFormContext<ExpertFormData>();

  const { resources } = watch();

  const handleRemoveResource = (resource: ExpertResource) =>
    setValue(
      'resources',
      resources.filter((r) => r.path !== resource.path),
      {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      },
    );

    const tooltipLabelFromErrorMessage = (errorMessage: Maybe<ResourceSyncError> | undefined) => {
      if (!errorMessage) {
        return "Sync Successful!"
      }
      if (errorMessage === ResourceSyncError.FileNotFound) {
        return "File Not Found";
      }
      if (errorMessage === ResourceSyncError.LimitReached) {
        return "Limit Reached";
      }
      if (errorMessage === ResourceSyncError.UnknownError) {
        return "Unknown Error";
      }
    }

    const getSyncStatus = (resource: ExpertResource) => {
      if (resource.lastSyncSuccessful || resource.lastSyncSuccessful === null) {
        return <Tooltip label={tooltipLabelFromErrorMessage(resource.lastSyncErrorMessage)}>
          <CheckCircle boxSize={4} color="green.500" />
          </Tooltip>;        
      }
      if (!resource.lastSyncSuccessful && resource.lastSyncErrorMessage) {
        return <Tooltip label={tooltipLabelFromErrorMessage(resource.lastSyncErrorMessage)}>
          <WarningTriangle boxSize={4} color="red.500" />
          </Tooltip>;
      }
    }

  const filesToRender = renderLimit ? resources.filter(resource => resource.source === IntegrationSourceType.GoogleDrive && !resource.parentFolder?.length).slice(0, renderLimit) : resources.filter(resource => resource.source === IntegrationSourceType.GoogleDrive && !resource.parentFolder?.length);
  return (
    <Stack spacing={0}>
      {filesToRender.map((resource) => (
        <Flex key={resource.id || resource.path} justifyContent="space-between" alignItems="center">
          <HStack spacing={2}>
            <GoogleFileTypeIcon type={resource.extensionType} />

            <Text fontSize="14px" fontWeight={500} color="baseBlack">
              {resource.extensionName}
            </Text>

          </HStack>

          <HStack spacing={2}>
            {getSyncStatus(resource)}
            <WarmNeutralEllipsisMenu
              iconOrientation="horizontal"
              options={[
                {
                  label: t('admin.aiAgent.experts.form.integrations.localUpload.remove'),
                  isHidden: !isEditingExpert,
                  onClick: () => handleRemoveResource(resource),
                },
              ]}
            />
          </HStack>
        </Flex>
      ))}
    </Stack>
  );
}
